'use client';

import { useEffect, useState } from 'react';
import { reportClientError } from '@/lib/clienterr';

// Last-resort boundary: a throw inside a layout (or the locale layout) escapes
// the per-segment error.tsx and would otherwise show Next's raw English
// "Application error: a client-side exception has occurred" white screen — which
// is exactly what iOS PWA users hit when an old cached bundle 404s a renamed
// chunk. global-error replaces the root HTML, so it must render <html>/<body>.
//
// Recovery: purge a possibly-poisoned service worker + caches, then reload —
// budgeted (once per 30s) so a genuinely persistent error can't spin forever.
export default function GlobalError({ error, reset }: { error: Error; reset: () => void }) {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    reportClientError('global-error', error);
    let willReload = false;
    try {
      const KEY = 'wow:global-reload';
      const now = Date.now();
      const [c, t] = (sessionStorage.getItem(KEY) || '0:0').split(':');
      let count = Number(c) || 0;
      const ts = Number(t) || 0;
      if (now - ts > 30000) count = 0;
      if (count < 1) {
        sessionStorage.setItem(KEY, `${count + 1}:${now}`);
        willReload = true;
      }
    } catch {
      willReload = true;
    }
    if (!willReload) {
      setStuck(true);
      return;
    }
    // Break a stale install: drop the SW + caches so the reload fetches a clean
    // bundle from the network. The push-only SW re-registers after recovery.
    (async () => {
      try {
        const regs = await navigator.serviceWorker?.getRegistrations?.();
        await Promise.all((regs || []).map((r) => r.unregister()));
      } catch {
        /* ignore */
      }
      try {
        const keys = await caches?.keys?.();
        await Promise.all((keys || []).map((k) => caches.delete(k)));
      } catch {
        /* ignore */
      }
      window.location.reload();
    })();
  }, []);

  return (
    <html lang="zh-Hant">
      <body
        style={{
          margin: 0,
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          padding: '0 24px',
          textAlign: 'center',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans TC", sans-serif',
          background: '#ffffff',
          color: '#18181b',
        }}
      >
        <p style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>頁面載入發生問題</p>
        <p style={{ fontSize: '14px', color: '#71717a', margin: 0, maxWidth: '20rem' }}>
          {stuck
            ? '自動修復仍未恢復。請將 App 從背景完全關閉再重新開啟。'
            : '正在自動清除快取並重新載入…'}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '4px',
            borderRadius: '12px',
            border: 'none',
            background: '#10b981',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            padding: '10px 20px',
          }}
        >
          重新載入
        </button>
      </body>
    </html>
  );
}
